import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@wasity/shared';
import { actAs, api, ApiError, onSessionLost } from './api';

export interface StaffMe {
  displayName: string;
  officeName?: string | null;
  licenceNo: string | null;
  membershipStatus: string;
  membershipPeriodEnd: string | null;
  /** False after "cancel renewal": the membership stays active until `membershipPeriodEnd`, then ends. */
  autoRenew?: boolean;
  slots: { used: number; max: number; free: number; membershipStatus: string; tier: { id: string; key: string; nameAr: string; nameEn: string; slots: number } | null };
}
export interface Me {
  user: { id: string; name: string; email: string; role: Role; locale: 'ar' | 'en'; phone: string | null; mustChangePassword: boolean };
  broker: StaffMe | null;
  evaluator: StaffMe | null;
}

export const ME_KEY = ['me'] as const;

export function useSession() {
  const qc = useQueryClient();
  // Any request answering "unauthenticated" means the cached `me` is a lie. Setting it to null rather
  // than refetching is deliberate: the guards read it synchronously and send the person to /login on
  // the next render, instead of leaving them on a page whose every query now fails.
  useEffect(() => onSessionLost(() => qc.setQueryData(ME_KEY, null)), [qc]);
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => api.post<{ user: unknown }>('/api/auth/sign-in/email', { email: input.email, password: input.password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/sign-out'),
    onSuccess: () => {
      // Oversight is per tab, and the tab outlives the session. Left behind, the next person to sign
      // in here — or the same admin, later — opened /broker and landed inside someone else's account
      // with the acting banner up, having never chosen it. It belongs to the session, so it ends with it.
      actAs.set(null);
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) => api.post('/api/me/change-password', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export const PASSWORD_RULES = [
  { key: 'length', test: (p: string) => p.length >= 12 },
  { key: 'upper', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lower', test: (p: string) => /[a-z]/.test(p) },
  { key: 'digit', test: (p: string) => /[0-9]/.test(p) },
  { key: 'special', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
] as const;
